/**
 * @author Ahmed Serag
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-08-01
 * @description Implementation of the matrices panel.
 * @filename matrices panel.tsx
 */
import { Types } from "@radivision/common";
import { List as GraphQlList } from "@radivision/graphql/lib/ts/graphql/list";
import { PreviewKind } from "@radivision/graphql/lib/ts/graphql/preview-type";

import React from "react";
import { graphql, QueryRenderer } from "react-relay";
import { ENVIRONMENT } from "../../relay/relay-environment";
import { RelayProps } from "../../relay/relay-props";
import { getCarouselItem } from "../../utilities/general";
import { RdvCarousel } from "../lists/rdv-carousel";
import { PanelNavbar } from "../page-framework/panel-navbar";

/**
 * The props of the carousel tab component
 *
 * @interface
 */
interface CarouselTabProps extends RelayProps {
  value: GraphQlList;
}

/**
 * A React component that renders the matrices panel slots like(companies and people in Home)
 *
 * @export
 * @class OneTabPanel
 * @extends {React.Component<CarouselTabProps>}
 */
export class CarouselTab extends React.Component<CarouselTabProps> {
  /**
   * Constructor
   *
   * @param {CarouselTabProps} props The component props.
   */
  constructor(props: CarouselTabProps) {
    super(props);
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <QueryRenderer
        environment={ENVIRONMENT}
        query={graphql`
          query carouselTabQuery($listID: ID!) {
            list(id: $listID) {
              hasErrors
            }
          }
        `}
        variables={{
          listID: this.props.value.id,
        }}
        render={({ error, props }) => {
          if (error) {
            return <div>{error.message}</div>;
          }
          if (props) {
            if (props.list.hasErrors) {
              return <div />;
            }
            if (props.list.node) {
              return this.renderList(props.list.node);
            }
          }
          return <div>Loading</div>;
        }}
      />
    );
  }

  /**
   * Returns a list rendered as a React element.
   *
   * @param {*} list The list to be rendered.
   *
   * @returns {React.ReactElement<any>} The React element containing the rendered list.
   */
  renderList(list: any): React.ReactElement<any> {
    const items: any[] = [];
    for (const edge of list.items.edges) {
      const item = edge.listItem.item;
      if (Types.isObject(item)) {
        items.push(
          getCarouselItem(
            edge,
            null,
            0,
            {
              containerWidthRatio: 1,
              numberOfItems: 1,
            },
            PreviewKind.SQUARE,
          ),
        );
      }
    }

    return (
      <div className="col-md-12" key={`curated-${list.id}`}>
        <h6 className="panel-subtitle">{list.subTitle}</h6>
        <PanelNavbar gridContent={[`tab-${list.id}`]} tabState={["active"]} tabText={[list.name]} hasSearch={false} />
        {/* <RdvCarousel
          carouselItemsList={items}
          numberOfItems={5}
          hasTitle={false}
          ratio={4 / 4}
          pageInfo={list.items.pageInfo}
          landingPageUrl={list.landingPageUrl}
        /> */}
      </div>
    );
  }
}
